import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Button, Tooltip, Space } from 'antd';
import { ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import TableIndex from '@shared/table/TableIndex';
import type { TableService } from '@shared/table/TableIndex';
import { formatDateTime } from '@utils/dateTime';
import { MultipleValue } from '@shared/table/Rules/component';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';

interface WebhookDeliveryRow {
  id: string;
  created_at?: string;
  event_type?: string;
  url?: string;
  status?: string;
  response_status?: number | string | null;
  retry_count?: number;
  [key: string]: unknown;
}

interface WebhookDeliveryService extends TableService {
  retry: (id: string) => Promise<unknown>;
}

interface WebhookDeliveryListProps {
  role?: string;
  service: WebhookDeliveryService;
}

const WebhookDeliveryList: React.FC<WebhookDeliveryListProps> = ({ role = 'operator', service }) => {
  const { t } = useTranslation();
  const [lastModified, setLastModified] = useState(0);

  const columns = useMemo(() => [
    {
      name: t('common.created_at'),
      selector: (row: WebhookDeliveryRow) => formatDateTime(row.created_at || ''),
      sortable: true,
      sortFieldName: 'webhook_deliveries.created_at',
      width: '180px',
    },
    {
      name: t('models.webhook_delivery.event_type'),
      selector: (row: WebhookDeliveryRow) => row.event_type,
    },
    {
      name: t('models.webhook_delivery.url'),
      selector: (row: WebhookDeliveryRow) => row.url,
      wrap: true,
    },
    {
      name: t('models.webhook_delivery.status'),
      selector: (row: WebhookDeliveryRow) => row.status,
      cell: (row: WebhookDeliveryRow) => {
        let color = 'default';
        if (row.status === 'success') color = 'success';
        if (row.status === 'failed') color = 'error';
        if (row.status === 'retrying') color = 'warning';
        return <Tag color={color}>{row.status}</Tag>;
      },
    },
    {
      name: t('models.webhook_delivery.response_status'),
      selector: (row: WebhookDeliveryRow) => row.response_status,
      width: '100px',
    },
    {
      name: t('models.webhook_delivery.retry_count'),
      selector: (row: WebhookDeliveryRow) => row.retry_count,
      width: '100px',
    },
    {
      name: t('common.table.actions'),
      cell: (row: WebhookDeliveryRow) => (
        <Space>
          {(row.status === 'failed' || row.status === 'retrying') && (
            <Tooltip title={t('common.button.retry')}>
              <Button
                icon={<ReloadOutlined />}
                size="small"
                onClick={async () => {
                  await service.retry(row.id);
                  setLastModified(Date.now());
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
      width: '100px',
    }
  ], [t, service]);

  const supportRules = useMemo(() => [
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'webhook_deliveries.created_at',
      defaultPinned: true,
    },
    {
      label: t('models.webhook_delivery.event_type'),
      component: MultipleValue,
      inputName: 'webhook_deliveries.event_type',
      createable: true,
    },
    {
      label: t('models.webhook_delivery.status'),
      component: MultipleValue,
      inputName: 'webhook_deliveries.status',
      defaultOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Success', value: 'success' },
        { label: 'Failed', value: 'failed' },
        { label: 'Retrying', value: 'retrying' },
      ],
    }
  ], [t]);

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.webhook_delivery.event_type'),
          t('models.webhook_delivery.url'),
        ],
      }}
      tableId={`${role}WebhookDeliveriesTable`}
      defaultQuery={{
        sorting_field: 'webhook_deliveries.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
    />
  );
};

export default WebhookDeliveryList;
